import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { 
  FaReceipt, 
  FaWifi, 
  FaNetworkWired, 
  FaBuilding, 
  FaUserTag, 
  FaFilter,
  FaPenToSquare,
  FaCalculator,
  FaMoneyBillWave,
  FaToggleOn,
  FaToggleOff,
  FaLayerGroup,
  FaXmark,
  FaCheck,
  FaBuildingColumns,
  FaGlobe
} from "react-icons/fa6";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../components/Header";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";

export const TaxesPage = () => {
  const [showSidebar, setShowSidebar] = useState(true);
  const [selectedPop, setSelectedPop] = useState("all");
  const [openModalEdit, setOpenModalEdit] = useState(false);

  // Data POP
  const pops = [
    { id: "all", name: "Semua POP (Global)" },
    { id: "pop-1", name: "POP Center - Jakarta" },
    { id: "pop-2", name: "POP Barat - Tangerang" },
    { id: "pop-3", name: "POP Selatan - Bogor" },
  ];

  // State Config Pajak General
  const [taxConfig, setTaxConfig] = useState({
    ppn: 11.00,
    bhp: 0.50,
    uso: 1.25,
    hotspot_tax_enabled: true,
    pppoe_tax_mode: "hybrid",
  });

  const [formConfig, setFormConfig] = useState({ ...taxConfig });

  // Sample Data Analytics & Breakdown Pajak Per Komponen (PPN, BHP, USO)
  const statsData = {
    totalOmset: 125000000,
    totalPajak: 15937500, // Total 12.75% dari Omset
    pajakBebanPerusahaan: 6375000,
    pajakBebanPelanggan: 9562500,
    // Breakdown Per Komponen
    breakdown: {
      ppn: 13750000,  // 11.00%
      bhp: 625000,    // 0.50%
      uso: 1562500,   // 1.25%
    }
  };

  // Sample Breakdown Pajak per POP dengan Rincian Komponen
  const popTaxBreakdown = [
    {
      id: "pop-1",
      name: "POP Center - Jakarta",
      omset: 60000000,
      totalPajak: 7650000,
      ppn: 6600000,
      bhp: 300000,
      uso: 750000,
      bebanPerusahaan: 3000000,
      bebanPelanggan: 4650000,
    },
    {
      id: "pop-2",
      name: "POP Barat - Tangerang",
      omset: 40000000,
      totalPajak: 5100000,
      ppn: 4400000,
      bhp: 200000,
      uso: 500000,
      bebanPerusahaan: 2100000,
      bebanPelanggan: 3000000,
    },
    {
      id: "pop-3",
      name: "POP Selatan - Bogor",
      omset: 25000000,
      totalPajak: 3187500,
      ppn: 2750000,
      bhp: 125000,
      uso: 312500,
      bebanPerusahaan: 1275000,
      bebanPelanggan: 1912500,
    },
  ];

  const formatIDR = (val) =>
    new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(val);

  const handleSaveConfig = (e) => {
    e.preventDefault();
    setTaxConfig({ ...formConfig });
    setOpenModalEdit(false);
  };

  return (
    <>
      <Helmet>
        <title>Pengaturan Pajak - Radius Trend</title>
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <Header showToggleSidebar={false} toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex flex-col">
            <h2 className="text-base md:text-xl font-display font-bold text-gray-100 tracking-tight">
              Pajak & Retribusi ISP
            </h2>
            <p className="text-xs text-slate-500 hidden md:block mt-0.5 uppercase tracking-[0.2em]">
              Kalkulasi Per Komponen (PPN, BHP, USO) & Skema Hybrid
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-slate-800/80 border border-slate-700/60 rounded-xl px-3 py-1.5">
              <FaFilter className="text-ocean-400 text-xs" />
              <select
                value={selectedPop}
                onChange={(e) => setSelectedPop(e.target.value)}
                className="bg-transparent text-xs text-slate-200 focus:outline-none font-medium cursor-pointer"
              >
                {pops.map((pop) => (
                  <option key={pop.id} value={pop.id} className="bg-slate-900 text-slate-200">
                    {pop.name}
                  </option>
                ))}
              </select>
            </div>

            <BtnDefault onClick={() => { setFormConfig({...taxConfig}); setOpenModalEdit(true); }}>
              <FaPenToSquare className="text-xs mr-1.5" />
              <span>Edit Pengaturan</span>
            </BtnDefault>
          </div>
        </Header>

        <main className="p-4 lg:p-6 space-y-6">
          
          {/* BARIS 1: RINGKASAN OMSET & TOTAL PAJAK BEBAN */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <article className="rounded-2xl p-5 bg-slate-900/50 border border-slate-700/30 backdrop-blur-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400 uppercase">Total Omset</span>
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
                  <FaMoneyBillWave />
                </div>
              </div>
              <p className="text-xl font-bold text-slate-100">{formatIDR(statsData.totalOmset)}</p>
              <p className="text-[11px] text-slate-500 mt-1">{selectedPop === "all" ? "Seluruh POP" : pops.find(p=>p.id===selectedPop)?.name}</p>
            </article>

            <article className="rounded-2xl p-5 bg-slate-900/50 border border-slate-700/30 backdrop-blur-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400 uppercase">Total Akumulasi Pajak</span>
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center">
                  <FaCalculator />
                </div>
              </div>
              <p className="text-xl font-bold text-slate-100">{formatIDR(statsData.totalPajak)}</p>
              <p className="text-[11px] text-blue-400 font-medium mt-1">Total tarif: {taxConfig.ppn + taxConfig.bhp + taxConfig.uso}%</p>
            </article>

            <article className="rounded-2xl p-5 bg-slate-900/50 border border-rose-500/30 backdrop-blur-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-rose-400 uppercase">Beban Perusahaan</span>
                <div className="w-8 h-8 rounded-lg bg-rose-500/10 text-rose-400 flex items-center justify-center">
                  <FaBuilding />
                </div>
              </div>
              <p className="text-xl font-bold text-slate-100">{formatIDR(statsData.pajakBebanPerusahaan)}</p>
              <p className="text-[11px] text-slate-400 mt-1">Dipotong dari pendapatan ISP</p>
            </article>

            <article className="rounded-2xl p-5 bg-slate-900/50 border border-purple-500/30 backdrop-blur-md">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-purple-400 uppercase">Beban Pelanggan</span>
                <div className="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center">
                  <FaUserTag />
                </div>
              </div>
              <p className="text-xl font-bold text-slate-100">{formatIDR(statsData.pajakBebanPelanggan)}</p>
              <p className="text-[11px] text-slate-400 mt-1">Dihitung pada invoice user</p>
            </article>
          </div>

          {/* BARIS 2: TOTAL PAJAK PER KOMPONEN (PPN, BHP, USO) */}
          <div>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
              Rincian Total Pajak Per Komponen
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              
              {/* Card PPN */}
              <div className="rounded-2xl p-4 bg-slate-900/40 border border-slate-700/40 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center shrink-0">
                  <FaReceipt className="text-xl" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 font-semibold uppercase">Total PPN</span>
                    <span className="text-[10px] bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded font-bold">{taxConfig.ppn}%</span>
                  </div>
                  <p className="text-lg font-bold text-slate-100 mt-0.5">{formatIDR(statsData.breakdown.ppn)}</p>
                </div>
              </div>

              {/* Card BHP */}
              <div className="rounded-2xl p-4 bg-slate-900/40 border border-slate-700/40 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                  <FaBuildingColumns className="text-xl" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 font-semibold uppercase">Total BHP Frekuensi</span>
                    <span className="text-[10px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded font-bold">{taxConfig.bhp}%</span>
                  </div>
                  <p className="text-lg font-bold text-slate-100 mt-0.5">{formatIDR(statsData.breakdown.bhp)}</p>
                </div>
              </div>

              {/* Card USO */}
              <div className="rounded-2xl p-4 bg-slate-900/40 border border-slate-700/40 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center shrink-0">
                  <FaGlobe className="text-xl" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 font-semibold uppercase">Total USO Kontribusi</span>
                    <span className="text-[10px] bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded font-bold">{taxConfig.uso}%</span>
                  </div>
                  <p className="text-lg font-bold text-slate-100 mt-0.5">{formatIDR(statsData.breakdown.uso)}</p>
                </div>
              </div>

            </div>
          </div>

          {/* BARIS 3: TABEL RINCIAN PAJAK PER POP + KOMPONEN */}
          <section className="rounded-2xl overflow-hidden bg-slate-900/50 border border-slate-700/40 backdrop-blur-md">
            <div className="p-5 border-b border-slate-700/50 flex justify-between items-center">
              <div>
                <h3 className="font-display text-base font-bold text-gray-100 flex items-center gap-2">
                  <FaLayerGroup className="text-ocean-400" /> Breakdown Pajak Per POP
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Distribusi omset, pajak per komponen (PPN, BHP, USO), serta pembagian beban biaya.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-300">
                <thead className="bg-slate-800/40 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-700/50">
                  <tr>
                    <th scope="col" className="px-6 py-4">Nama POP</th>
                    <th scope="col" className="px-6 py-4">Total Omset</th>
                    <th scope="col" className="px-6 py-4">PPN ({taxConfig.ppn}%)</th>
                    <th scope="col" className="px-6 py-4">BHP ({taxConfig.bhp}%)</th>
                    <th scope="col" className="px-6 py-4">USO ({taxConfig.uso}%)</th>
                    <th scope="col" className="px-6 py-4">Total Pajak</th>
                    <th scope="col" className="px-6 py-4">Beban Corp / Cust</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {popTaxBreakdown.map((pop) => (
                    <tr key={pop.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4 font-semibold text-gray-200">{pop.name}</td>
                      <td className="px-6 py-4 font-medium text-slate-200">{formatIDR(pop.omset)}</td>
                      <td className="px-6 py-4 text-xs text-blue-400">{formatIDR(pop.ppn)}</td>
                      <td className="px-6 py-4 text-xs text-emerald-400">{formatIDR(pop.bhp)}</td>
                      <td className="px-6 py-4 text-xs text-purple-400">{formatIDR(pop.uso)}</td>
                      <td className="px-6 py-4 font-bold text-slate-100">{formatIDR(pop.totalPajak)}</td>
                      <td className="px-6 py-4 text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="text-rose-400 font-semibold" title="Beban Perusahaan">{formatIDR(pop.bebanPerusahaan)}</span>
                          <span className="text-slate-600">/</span>
                          <span className="text-purple-400 font-semibold" title="Beban Pelanggan">{formatIDR(pop.bebanPelanggan)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

        </main>
      </DashboardLayout>

      {/* MODAL FORM EDIT SETTING */}
      {openModalEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl p-6 border border-slate-700/80 space-y-5 bg-slate-900">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="font-bold text-gray-100 text-base">Edit Pengaturan Pajak ISP</h3>
              <button onClick={() => setOpenModalEdit(false)} className="text-slate-400 hover:text-white">
                <FaXmark className="text-lg" />
              </button>
            </div>

            <form onSubmit={handleSaveConfig} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase text-slate-400 mb-2">
                  Tarif Pajak General (%)
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <span className="text-xs text-slate-500 mb-1 block">PPN (%)</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formConfig.ppn}
                      onChange={(e) => setFormConfig({ ...formConfig, ppn: parseFloat(e.target.value) || 0 })}
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-ocean-400"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 mb-1 block">BHP (%)</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formConfig.bhp}
                      onChange={(e) => setFormConfig({ ...formConfig, bhp: parseFloat(e.target.value) || 0 })}
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-ocean-400"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 mb-1 block">USO (%)</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formConfig.uso}
                      onChange={(e) => setFormConfig({ ...formConfig, uso: parseFloat(e.target.value) || 0 })}
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-ocean-400"
                    />
                  </div>
                </div>
              </div>

              <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/60 flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-bold text-slate-200">Aktifkan Pajak Hotspot</h4>
                  <p className="text-[11px] text-slate-400">Selalu dibebankan ke perusahaan</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormConfig({ ...formConfig, hotspot_tax_enabled: !formConfig.hotspot_tax_enabled })}
                  className="text-2xl text-ocean-400 focus:outline-none"
                >
                  {formConfig.hotspot_tax_enabled ? <FaToggleOn className="text-emerald-400" /> : <FaToggleOff className="text-slate-600" />}
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-slate-400 mb-2">
                  Metode Pembebanan PPPoE
                </label>
                <select
                  value={formConfig.pppoe_tax_mode}
                  onChange={(e) => setFormConfig({ ...formConfig, pppoe_tax_mode: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-ocean-400 cursor-pointer"
                >
                  <option value="company">Beban Perusahaan (All Company)</option>
                  <option value="customer">Beban Pelanggan (All Customer)</option>
                  <option value="hybrid">Hybrid (Atur per Paket / per Pelanggan)</option>
                </select>
              </div>

              <div className="pt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setOpenModalEdit(false)}
                  className="px-4 py-2 text-xs font-semibold text-slate-400 hover:bg-slate-800 rounded-xl"
                >
                  Batal
                </button>
                <BtnDefault type="submit" className="!py-2 !px-4">
                  Simpan Perubahan
                </BtnDefault>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};